import { Injectable, InjectionToken } from '@angular/core';
import { BaseService } from './base.service';
import { HttpClient } from '@angular/common/http';
import { Observable, of, UnaryFunction, pipe, throwError, Subject, Subscription } from 'rxjs';
import { catchError, map, switchMap, take, tap, shareReplay } from 'rxjs/operators'
import { ControlUser } from '../model/controlUser';
import { ItemsList } from '../model/itemsList';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EntityId } from '../model/entityId';
import firebase from "firebase/compat/app";
import { Log } from '../util/log';
import { ControlUserInstallation } from '../model/controlUserInstallation';
import { DateTime } from 'luxon';
import { UserOwnerRel } from '../model/userOwnerRel';
import { OwnerData } from '../model/ownerData';
import { AccessMethod } from '../model/accessMethod';
import { AccessMethods } from '../model/accessMethods';
import { UserPermissions } from '../model/userPermissions';
import { TimeSlotHelper } from '../util/timeSlotHelper';
import { UserPermissionsList } from '../model/userPermissionsList';
import { RemoteHelper } from '../util/remoteHelper';
import { ActivationCode } from '../model/activationCode';
import { environment } from 'src/environments/environment';
import { AngularFireModule } from '@angular/fire/compat';
import { OwnerRegistrationData } from '../model/ownerRegistrationData';
import { Group } from '../model/group';
import { GroupPermissionsList } from '../model/groupPermissionsList';
import { GroupPermissions } from '../model/groupPermissions';
import { UserOwnerRelData } from '../model/userOwnerRelData';
import { AliasOwnerRel } from '../model/AliasOwnerRel';
import { AliasRegistrationData } from '../model/AliasRegistrationData';
import { DateHelper } from '../util/dateHelper';
import { EntityCount } from '../model/entityCount';
import { operationLogEvent } from '../model/operationLogEvent';
import { EmailData } from '../model/EmailData';
import { EmailDefaultData } from '../model/EmailDefaultData';
import { NewUserRequest } from '../model/newUserRequest';
import { NewUserRequestResult } from '../model/NewUserRequestResult';
import { NewUserRequestPermissionType } from '../model/NewUserRequestPermissionType';

// export type AuthPipeGenerator = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot) => AuthPipe;
// export type AuthPipe = UnaryFunction<Observable<User|null>, Observable<boolean|any[]>>;

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseService implements CanActivate {

  public meUser: ControlUser;

  public isUserAlias: boolean = false;
  public checkedIfAlias: boolean = false;
  public meAliasUser: AliasOwnerRel;

  public loadedUser: ControlUser;
  public loadedUserAlias: ControlUser;

  public loggedUser: Subject<ControlUser> = new Subject<ControlUser>();
  public meUserObservable = this.loggedUser.asObservable();

  private meObservable;
  private meAliasObservable;
  private userInstallationObservable;
  private ownersObservable;
  private createUpdateOwnerObservable;
  private AliasesObservable;
  private getAvailableOperatorsObservable;
  private ownerUsersObservable;
  private ownerGroupsObservable;
  private ownerDeviceObservable;
  private ownerDataObservable;
  private userObservable;
  private aliasObservable;
  private postAliasFeaturesObservable;
  private aliasFeaturesObservable;
  private groupObservable;
  private ownerGroupObservable;
  private group;
  private groupPermissionsObservable;
  private userOwnerObservable;
  private userAccessMethodsObservable;
  private userAccessMethodsObservablePost;
  private userPermissionsObservable;
  private userPermissionsCsvObservable;
  private getUserOwnerRelObservable;
  private updateuserOwnerRelObservable;
  private createUserOwnerRelObservable;
  private userOwnerRelForceObservable;
  private userOwnerRelMassive;
  private activationCodesObservable;
  private newActivationCodesObservable;
  private updatedActivationCodesObservable;
  private operationLogObservable;
  private operationLogCountObservable;
  private operationLogByOperatorObservable;
  private operationLogByOperatorCountObservable;
  private operationLogBySerialObservable;
  private operationLogBySerialCountObservable;
  private getOwnerDefaultEmailObservable;
  private setOwnerDefaultEmailObservable;
  private createNewUserRequestObservable;
  private getNewUserRequestsObservable;
  private acceptRejectNewUserRequestObservable;
  private deleteRequestObservable;
  private deviceUsersObservable;
  private createNewUserRequestPermissionTypeObservable;
  private getNewUserRequestPermissionTypeObservable;
  private deleteNewUserRequestPermissionTypeObservable;

  private emailSentObservable;

  private headerRefresh = new Subject<void>();

  refresh = this.headerRefresh.asObservable();


  private aliasPermissionCache: Map<string, boolean> = new Map();
  private permissionsPreloaded = false;

  private aliasReadyPromise: Promise<void> | null = null;

  constructor(http: HttpClient, afAuth: AngularFireAuth, private router: Router) {
    super(http, afAuth);

    this.meUser = JSON.parse(localStorage.getItem("controlUser"));

    Log.debug('meUser: ' + JSON.stringify(this.meUser));
    // Log.debug('firebaseUser: ' + JSON.stringify(this.afAuth.auth.currentUser));
  }

  async login(email: string, password: string) {

    return this.afAuth.signInWithEmailAndPassword(email, password)
      .then(async (userCredential) => {

        this.setFirebaseUser(userCredential.user);

        // Sign-in successful.
        return userCredential.user;
      });
  }

  async register(email: string, password: string) {

    return this.afAuth.createUserWithEmailAndPassword(email, password)
      .then(async (userCredential) => {

        Log.debug("userCredential: " + JSON.stringify(userCredential));

        this.setFirebaseUser(userCredential.user);

        // Registration successful.
        return userCredential.user;
      })
  }

  async registerUserInFirebase(email: string, password: string) {

    return this.workerAuth.createUserWithEmailAndPassword(email, password)
      .then(async (userCredential) => {

        Log.debug("userCredential: " + JSON.stringify(userCredential));

        // Registration successful.
        return userCredential.user;
      })
  }

  async resetPassword(email: string) {

    return this.afAuth.sendPasswordResetEmail(email)
      .then(async () => {

        // reset successful.
        return true;
      });
  }

  async logout() {
    localStorage.removeItem('controlUser');

    this.meUser = null;
    this.meAliasUser = null;
    this.meObservable = null;
    this.meAliasObservable = null;
    this.aliasPermissionCache.clear();
    this.permissionsPreloaded = false;
    this.isUserAlias = false;
    this.checkedIfAlias = false;

    this.aliasReadyPromise = null;

    return this.afAuth.signOut();
  }


  get isLoggedIn(): boolean {

    return this.isFirebaseLogged() && this.meUser != null;

    // return this.loggedBool.asObservable().pipe(
    //   tap(logged => Log.debug('isLoggedIn: ' + logged))
    // );
  }

  get isLoggedInMeUser(): boolean {

    return this.meUser != null;

    // return this.loggedBool.asObservable().pipe(
    //   tap(logged => Log.debug('isLoggedIn: ' + logged))
    // );
  }

  get isAdmin(): boolean {
    return this.isLoggedIn && this.meUser.userType == ControlUser.UserTypeAdmin;
  }



  get isService(): boolean {
    return this.isLoggedIn && this.meUser.userType == ControlUser.UserTypeService;
  }


  get isAlias(): boolean {
    return this.meUser.userType == ControlUser.UserTypeAlias;
  }

  async isAliasAsync(): Promise<boolean> {
    // If user already exists, no need to wait.
    if (this.meUser) {
      return this.meUser.userType === ControlUser.UserTypeAlias;
    }

    // Wait until the first emission from getMe()
    const user = await this.getMe().toPromise();
    return this.isLoggedIn && (user.userType === ControlUser.UserTypeAlias);
  }

  getMe(): Observable<ControlUser> {
    if (this.meUser) {
      setTimeout(() => this.headerRefresh.next(), 0);
      return of(this.meUser);

    } else if (this.meObservable) {
      setTimeout(() => this.headerRefresh.next(), 0);
      return this.meObservable;

    } else {
      Log.debug('getting user');

      this.meObservable = this.getData<ControlUser>('me').pipe(
        shareReplay(1),
        map((data) => {
          this.meObservable = null;

          Log.debug('me: ' + JSON.stringify(data));

          this.meUser = Object.create(ControlUser.prototype);
          this.meUser.deserialize(data);

          this.loggedUser.next(this.meUser);
          localStorage.setItem("controlUser", JSON.stringify(this.meUser));

          // se non è alias invoco aggiornamento header
          if (this.meUser.userType != ControlUser.UserTypeAlias) {
            setTimeout(() => this.headerRefresh.next(), 0);
          }

          Log.debug('meUser: ' + JSON.stringify(this.meUser));

          return this.meUser;
        }),
        catchError(err => {
          Log.debug('me error :' + err);
          this.meObservable = null;
          return throwError(err);
        })
      );

      return this.meObservable;
    }
  }



  ensureAliasReady(): Promise<void> {
    // If already running or completed, reuse the promise
    if (this.aliasReadyPromise) {
      return this.aliasReadyPromise;
    }

    this.aliasReadyPromise = new Promise((resolve, reject) => {
      // Fetch or return cached user
      this.getMe().pipe(
        take(1),
        switchMap(user => {
          if (!user) {
            throw new Error('getMe() did not return a user');
          }
          if (user.userType === ControlUser.UserTypeAlias) {
            this.isUserAlias = true;
            return this.getMeAsAlias();
          } else {
            this.isUserAlias = false;
            this.checkedIfAlias = true;
            setTimeout(() => this.headerRefresh.next(), 0);
            return of(null);
          }
        })
      ).subscribe({
        next: () => resolve(),
        error: (err) => {
          this.aliasReadyPromise = null;
          reject(err);
        }
      });
    });

    return this.aliasReadyPromise;
  }

  getMeAsAlias(): Observable<AliasOwnerRel> {
    if (this.meAliasUser) {
      return of(this.meAliasUser);
    }

    if (!this.meAliasObservable) {
      Log.debug('getting meAlias');

      this.meAliasObservable = this.getData<AliasOwnerRel>('meAsAlias').pipe(
        switchMap((meAsAliasData) => {
          return this.getAliasByUid(meAsAliasData.uid).pipe(
            map((aliasData) => {
              // Create and initialize the alias user.
              this.meAliasUser = Object.create(ControlUser.prototype);
              // Use the data from the "meAsAlias" endpoint (which contains availableFeature)
              this.meAliasUser.deserialize(aliasData);
              // // Manually assign availableFeature if it wasn’t handled in deserialize.
              // if (aliasData.availableFeature) {
              //   this.meAliasUser.availableFeature = aliasData.availableFeature;
              // }
              this.checkedIfAlias = true;
              setTimeout(() => this.headerRefresh.next(), 0);
              this.meAliasObservable = null;
              return this.meAliasUser;
            })
          );
        }),
        catchError(err => {
          Log.debug('me error: ' + err);
          this.meAliasObservable = null;
          return throwError(err);
        }),
        shareReplay(1)
      );
    }

    return this.meAliasObservable;
  }




  async preloadAliasPermissions(): Promise<void> {
    await this.ensureAliasReady();

    this.aliasPermissionCache.clear(); // reset

    if (this.isUserAlias && this.meAliasUser?.availableFeature) {
      for (const feature of this.meAliasUser.availableFeature) {
        this.aliasPermissionCache.set(feature, true);
      }
    }

    this.permissionsPreloaded = true;
  }

  async checkAliasPermissions(feature: string): Promise<boolean> {

    Log.debug("\n\n\t Checking for " + feature + " permission")

    if (!this.permissionsPreloaded) {
      await this.preloadAliasPermissions();
    }

    if (this.aliasPermissionCache.get("All")) {
      return true;
    }

    if (this.isUserAlias) {
      return this.aliasPermissionCache.get(feature) ?? false;
    }

    return true; // non-alias users always allowed
  }

  checkAliasPermissionsNow(feature: string): boolean {
    return this.meAliasUser.availableFeature.includes(feature);

  }


  async checkAliasHomePage(): Promise<string | undefined> {
    await this.ensureAliasReady();
    if (this.isUserAlias && this.meAliasUser && Array.isArray(this.meAliasUser.availableFeature)) {
      // Find the first feature that ends with "View"
      const feature = this.meAliasUser.availableFeature.find(f => f.endsWith("View"));

      // da gestire a parte dato che è l'unico senza "View" finale
      if (feature === undefined) {
        if (this.meAliasUser.availableFeature[0] == "accept-requests") {
          return "accept-requests";
        }
      }

      Log.debug("Alias homepage feature found: " + feature);
      return feature;
    } else {
      return "not-alias";
    }
    Log.debug("No alias homepage feature found or user is not alias");
    return undefined;
  }


  getUser(uid: String): Observable<ControlUser> {

    if (this.userObservable) {

      return this.userObservable;

    } else {

      Log.debug('getting user: ' + uid);

      this.userObservable = this.getData<ControlUser>('user/' + uid).pipe(
        map((data) => {

          this.userObservable = null;

          Log.debug('user: ' + JSON.stringify(data));

          this.loadedUser = Object.create(ControlUser.prototype);
          this.loadedUser.deserialize(data);

          Log.debug('user: ' + JSON.stringify(this.loadedUser));

          return this.loadedUser;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getUser error :' + err);
          this.userObservable = null;
          return throwError(err);
        }
        ));

      return this.userObservable;
    }
  }


  getAlias(id: String): Observable<AliasOwnerRel> {

    if (this.aliasObservable) {

      return this.aliasObservable;

    } else {
      Log.debug('getting alias: ' + id);

      this.aliasObservable = this.getData<AliasOwnerRel>('alias/' + id).pipe(
        map((data) => {

          this.aliasObservable = null;

          Log.debug('alias ' + JSON.stringify(data));

          this.loadedUserAlias = Object.create(AliasOwnerRel.prototype);
          this.loadedUserAlias.deserialize(data);

          Log.debug('alias ' + JSON.stringify(this.loadedUserAlias));

          return this.loadedUserAlias;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getAlais error :' + err);
          this.aliasObservable = null;
          return throwError(err);
        }
        ));

      return this.aliasObservable;
    }
  }


  getAliasByUid(uid: string): Observable<AliasOwnerRel> {
    if (this.aliasObservable) {
      return this.aliasObservable;
    } else {
      Log.debug('getting alias: ' + uid);

      this.aliasObservable = this.getData<AliasOwnerRel>('alias/user/' + uid).pipe(
        map((data) => {
          this.aliasObservable = null;
          this.loadedUserAlias = Object.create(AliasOwnerRel.prototype);
          this.loadedUserAlias.deserialize(data);
          return data; // or return this.loadedUserAlias, if that is what you prefer.
        }),
        catchError(err => {
          Log.debug('getAlias error :' + err);
          this.aliasObservable = null;
          return throwError(err);
        }),
        shareReplay(1)
      );
      return this.aliasObservable;
    }
  }


  postAliasFeatures(id, aliasOwnerRel): Observable<any> {

    if (this.postAliasFeaturesObservable) {

      return this.postAliasFeaturesObservable;

    } else {
      // "alias/{id}/features" 
      this.postAliasFeaturesObservable = this.postData<any>('alias/' + id + "/features", JSON.stringify(aliasOwnerRel)).pipe(
        map((data) => {

          this.postAliasFeaturesObservable = null;

          let aliasFeatures = Object.create(AliasOwnerRel.prototype);
          aliasFeatures.deserialize(data);
          return aliasFeatures;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getAlais error :' + err);
          this.postAliasFeaturesObservable = null;
          return throwError(err);
        }
        ));

      return this.postAliasFeaturesObservable;
    }
  }


  updateAliasPublicName(aliasId: string, publicName: string) {

    // POST "alias/{id}/publicName/{publicName}"
    return this.postData<any>("alias/" + aliasId + "/publicName/" + publicName, null).pipe(
      map((data) => {

        this.postAliasFeaturesObservable = null;

        let aliasFeatures = Object.create(AliasOwnerRel.prototype);
        aliasFeatures.deserialize(data);
        return aliasFeatures;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('getAlais error :' + err);
        this.postAliasFeaturesObservable = null;
        return throwError(err);
      }
      ));
  }



  public updateAccessMethods(accessMethods: AccessMethods, userOwnerId: number): Observable<EntityId> {

    for (let i = 0; i < accessMethods.methods.length; i++) {
      if (accessMethods.methods[i].type == AccessMethod.EVENT_TYPE_REMOTE) {
        accessMethods.methods[i].code = RemoteHelper.jcmCodeToStandardCode(accessMethods.methods[i].code);
      }
    }

    this.userAccessMethodsObservablePost = this.postData<EntityId>("userOwner/" + userOwnerId + "/accessMethods", JSON.stringify(accessMethods)).pipe(
      map((data) => {
        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('updateAccessMethods error :' + err);
        this.userAccessMethodsObservablePost = null;
        return throwError(err);
      }
      ));

    // reconvert from standard code to jcm code to not alter the code in the viewed page
    for (let i = 0; i < accessMethods.methods.length; i++) {
      if (accessMethods.methods[i].type == AccessMethod.EVENT_TYPE_REMOTE) {
        accessMethods.methods[i].code = RemoteHelper.standardCodeToJcmCode(accessMethods.methods[i].code).toString();
      }
    }

    return this.userAccessMethodsObservablePost;
  }

  public updateUserPermissions(permissions: UserPermissionsList, userOwnerId: number): Observable<EntityId> {
    Log.debug("Permissions update: ");
    Log.debug(permissions);
    this.userPermissionsObservable = this.postData<EntityId>("userOwner/" + userOwnerId + "/permissionsList", JSON.stringify(permissions)).pipe(
      map((data) => {
        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('updateUserPermissions error :' + err);
        this.userPermissionsObservable = null;
        return throwError(err);
      }
      ));
    return this.userPermissionsObservable;
  }

  public updateGroupPermissions(groupId: number, ownerUid: string, permissions: GroupPermissionsList): Observable<EntityId> {
    // "group/{id}/owner/{ownerUid}/permissionsList"
    Log.debug("Group permissions update: ");
    Log.debug(permissions);
    this.groupPermissionsObservable = this.postData<EntityId>("group/" + groupId + "/owner/" + ownerUid + "/permissionsList", JSON.stringify(permissions)).pipe(
      map((data) => {
        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('updateGroupPermissions error :' + err);
        this.groupPermissionsObservable = null;
        return throwError(err);
      }
      ));
    return this.groupPermissionsObservable;
  }


  public getOwners(): Observable<ControlUser[]> {

    if (this.ownersObservable) {
      Log.debug('getOwners data already loading');
      return this.ownersObservable;

    } else {

      Log.debug('getOwners no data');
      this.ownersObservable = this.getData<ItemsList<ControlUser[]>>('user/owner').pipe(
        map((data) => {
          this.ownersObservable = null;
          Log.debug('getOwners: ' + JSON.stringify(data));
          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new ControlUser().deserialize(item));
          Log.debug('owners: ' + JSON.stringify(o.items));
          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwners error :' + err);
          this.ownersObservable = null;
          return throwError(err);
        }
        ));
      return this.ownersObservable;
    }
  }


  public createUpdateOwnerData(uid, owner): Observable<ControlUser> {
    // post /webAdmin/v1/user/owner/{uid}/data

    if (this.createUpdateOwnerObservable) {
      Log.debug('getOwners data already loading');
      return this.createUpdateOwnerObservable;

    } else {

      Log.debug('getOwners no data');
      this.createUpdateOwnerObservable = this.postData<ItemsList<EntityId>>("user/owner/" + uid + "/data", owner).pipe(
        map((data) => {
          let entity = Object.create(EntityId.prototype);
          entity.deserialize(data);
          return entity;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwners error :' + err);
          this.createUpdateOwnerObservable = null;
          return throwError(err);
        }
        ));
      return this.createUpdateOwnerObservable;
    }
  }



  // GET /webAdmin/v1/alias/owner/{ownerUid}
  public getOwnerAliases(ownerUid: string): Observable<AliasOwnerRel[]> {

    if (this.AliasesObservable) {
      Log.debug('getOwnerAliases data already loading');
      return this.AliasesObservable;

    } else {

      Log.debug('getOwnerAliases no data');
      this.AliasesObservable = this.getData<ItemsList<AliasOwnerRel[]>>('alias/owner/' + ownerUid).pipe(
        map((data) => {
          this.AliasesObservable = null;
          Log.debug('getOwnerAliases: ' + JSON.stringify(data));
          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new AliasOwnerRel().deserialize(item));
          Log.debug('aliases: ' + JSON.stringify(o.items));
          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwnerAliases error :' + err);
          this.AliasesObservable = null;
          return throwError(err);
        }
        ));
      return this.AliasesObservable;
    }
  }



  public getAvailableOperators(ownerUid: string): Observable<AliasOwnerRel[]> {
    //  GET /public/v1/availableOperators/{ownerUid}
    if (this.getAvailableOperatorsObservable) {
      Log.debug('getAvailableOperators data already loading');
      return this.getAvailableOperatorsObservable;

    } else {

      Log.debug('getAvailableOperators no data');
      this.getAvailableOperatorsObservable = this.getPublicData<ItemsList<AliasOwnerRel[]>>('availableOperators/' + ownerUid).pipe(
        map((data) => {
          this.getAvailableOperatorsObservable = null;
          Log.debug('getAvailableOperators: ' + JSON.stringify(data));
          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new AliasOwnerRel().deserialize(item));
          Log.debug('aliases: ' + JSON.stringify(o.items));
          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getAvailableOperators error :' + err);
          this.getAvailableOperatorsObservable = null;
          return throwError(err);
        }
        ));
      return this.getAvailableOperatorsObservable;
    }
  }



  // POST /webAdmin/v1/alias/owner/{ownerUid}/user/email/{email}
  linkAliasOwner(ownerUid: string, email: string): Observable<AliasOwnerRel> {
    let linkedAlias = this.postData<AliasOwnerRel>('alias/owner/' + ownerUid + '/user/email/' + email, null).pipe(
      map((data) => {

        Log.debug('create alias: ' + JSON.stringify(data));

        linkedAlias = Object.create(AliasOwnerRel.prototype);
        linkedAlias.deserialize(data);

        return linkedAlias;
      }
      )).pipe(catchError(err => {
        Log.debug('linkAliasOwner error :' + err);
        return throwError(err);
      }
      ));

    return linkedAlias;
  }

  //POST alias/owner/{ownerUid}
  createAliasUser(ownerUid: string, aliasRegistrationData: AliasRegistrationData): Observable<AliasOwnerRel> {
    let newAliasUser = this.postData<AliasOwnerRel>('alias/owner/' + ownerUid, aliasRegistrationData).pipe(
      map((data) => {

        Log.debug('createAliasUser: ' + JSON.stringify(data));

        newAliasUser = Object.create(AliasOwnerRel.prototype);
        newAliasUser.deserialize(data);

        return newAliasUser;
      }
      )).pipe(catchError(err => {
        Log.debug('createAliasUser error :' + err);
        return throwError(err);
      }
      ));

    return newAliasUser;
  }


  unlinkAliasFromOwner(aliasUid) {
    // aliasUid = aliasUid.toString();
    // DELETE /webAdmin/v1/alias/{id}
    let aliasToDelete = this.deleteData<EntityId>("alias/" + aliasUid).pipe(
      map((data) => {
        Log.debug("deleteActivationCode code response");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      ));
    return aliasToDelete;
  }

  public getOwnerUsers(ownerUid: string): Observable<UserOwnerRel[]> {

    if (this.ownerUsersObservable) {
      Log.debug('getOwnerUsers data already loading');
      return this.ownerUsersObservable;
    } else {
      Log.debug('getOwnerUsers no data');
      this.ownerUsersObservable = this.getData<ItemsList<UserOwnerRel[]>>('owner/' + ownerUid + '/user').pipe(
        map((data) => {
          this.ownerUsersObservable = null;
          Log.debug('getOwnerUsers: ' + JSON.stringify(data));
          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new Group().deserialize(item));
          Log.debug('owner users: ' + JSON.stringify(o.items));
          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwnerUsers error :' + err);
          this.ownerUsersObservable = null;
          return throwError(err);
        }
        ));
      return this.ownerUsersObservable;
    }
  }

  public getDeviceUsers(serial: string, ownerUid: string): Observable<UserOwnerRel[]> {

    if (this.deviceUsersObservable) {

      Log.debug('getDeviceUsers already loading');

      return this.deviceUsersObservable;

    } else {

      // GET owner/{ownerUid}/device/{serial}/user

      // "device/{serial}/ota"
      this.deviceUsersObservable = this.getData<ItemsList<UserOwnerRel[]>>("owner/" + ownerUid + "/device/" + serial + "/user").pipe(
        map((data) => {

          this.deviceUsersObservable = null;
          Log.debug('getDeviceUsers : ' + JSON.stringify(data));
          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new Group().deserialize(item));
          Log.debug('getDeviceUsers  : ' + JSON.stringify(o.items));
          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getDeviceUsers  error :' + err);
          this.deviceUsersObservable = null;
          return throwError(err);
        }
        ));
      return this.deviceUsersObservable;
    }
  }

  public getOwnerGroups(ownerUid: string): Observable<Group[]> {

    if (this.ownerGroupsObservable) {
      Log.debug('getOwnerGroups data already loading');
      return this.ownerGroupsObservable;
    } else {
      Log.debug('getOwnerGroups no data');
      this.ownerGroupsObservable = this.getData<ItemsList<Group[]>>('group/owner/' + ownerUid).pipe(
        map((data) => {
          this.ownerGroupsObservable = null;
          Log.debug('getOwnerGroups: ' + JSON.stringify(data));
          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new UserOwnerRel().deserialize(item));
          Log.debug('owner groups: ' + JSON.stringify(o.items));
          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwnerGroups error :' + err);
          this.ownerGroupsObservable = null;
          return throwError(err);
        }
        ));
      return this.ownerGroupsObservable;
    }
  }



  public getOwnerOfDevice(serial: string): Observable<ControlUser> {
    Log.debug('getOwnerOfDevice loading');
    if (this.ownerDeviceObservable) {
      Log.debug('getOwnerOfDevice data already loading');
      return this.ownerDeviceObservable;
    } else {
      Log.debug('getOwnerOfDevice no data');
      this.ownerDeviceObservable = this.getData<ItemsList<ControlUser>>('device/' + serial + '/owner').pipe(
        map((data) => {
          this.ownerDeviceObservable = null;
          Log.debug('getOwnerOfDevice: ' + JSON.stringify(data));
          const o = Object.create(ControlUser.prototype);
          Object.assign(o, data);
          o.deserialize(data);
          Log.debug('owner: ' + JSON.stringify(o));
          return o;
        }
        )).pipe(catchError(err => {
          Log.debug('getOwnerOfDevice error :' + err);

          this.ownerDeviceObservable = null;

          if (err.error.code == 404) {
            Log.debug("No owner found but that's fine");
            return null;
          } else {
            return throwError(err);
          }
        }
        ));
      return this.ownerDeviceObservable;
    }
  }

  createUser(email: string): Observable<ControlUser> {

    let user: ControlUser = new ControlUser();
    user.email = email;
    user.username = email;

    this.userObservable = this.putData<ControlUser>('userFromFirebase/' + this.getFirebaseUid(), user).pipe(
      map((data) => {

        this.userObservable = null;

        Log.debug('createUser: ' + JSON.stringify(data));

        this.meUser = Object.create(ControlUser.prototype);
        this.meUser.deserialize(data);

        localStorage.setItem("controlUser", JSON.stringify(this.meUser));

        Log.debug('meUser: ' + JSON.stringify(this.meUser));

        return this.meUser;
      }
      )).pipe(catchError(err => {
        Log.debug('createUser error :' + err);
        this.userObservable = null;
        return throwError(err);
      }
      ));

    return this.userObservable;
  }

  createGroup(group: Group): Observable<Group> {
    this.groupObservable = this.postData<Group>('group', group).pipe(
      map((data) => {

        this.groupObservable = null;

        Log.debug('creategroup: ' + JSON.stringify(data));
        group = Object.create(Group.prototype);
        group.deserialize(data);

        // localStorage.setItem("Group", JSON.stringify(this.meGroup));

        Log.debug('group: ' + JSON.stringify(group));

        return group;
      }
      )).pipe(catchError(err => {
        Log.debug('creategroup error :' + err);
        this.groupObservable = null;
        return throwError(err);
      }
      ));

    return this.groupObservable;
  }

  getGroup(groupId: number): Observable<Group> {
    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/group/{id}
    this.groupObservable = this.getData<Group>("group/" + groupId).pipe(
      map((data) => {
        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        Log.debug("getgroupObservable");
        Log.debug(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('getgroupObservable error :' + err);
        this.groupObservable = null;
        return throwError(err);
      }
      ));
    return this.groupObservable;
  }

  getOwnerGroup(groupId: number, ownerUid: string): Observable<Group> {
    //           GET   group/{id}/owner/{ownerUid}
    this.ownerGroupObservable = this.getData<Group>("group/" + groupId + "/owner/" + ownerUid).pipe(
      map((data) => {
        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        Log.debug("ownerGetgroupObservable");
        Log.debug(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('ownerGetgroupObservable error :' + err);
        this.ownerGroupObservable = null;
        return throwError(err);
      }
      ));
    return this.ownerGroupObservable;
  }

  getUserOwnerRel(ownerUid: number): Observable<any> {
    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/userOwnerRel/{userOwnerId}
    this.getUserOwnerRelObservable = this.getData<EntityId>("userOwnerRel/" + ownerUid).pipe(
      map((data) => {
        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        Log.debug("user owner rel");
        Log.debug(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('getUserOwnerRel error :' + err);
        this.getUserOwnerRelObservable = null;
        return throwError(err);
      }
      ));
    return this.getUserOwnerRelObservable;
  }

  createUserOwnerRel(userOwnerRel: UserOwnerRelData, ownerUid: string): Observable<any> {
    //https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/
    this.createUserOwnerRelObservable = this.postData<EntityId>("userOwnerRel/" + ownerUid, userOwnerRel).pipe(
      map((data) => {

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);

        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('createUserOwnerRel error :' + err);
        this.createUserOwnerRelObservable = null;
        return throwError(err);
      }
      ));

    return this.createUserOwnerRelObservable;
  }

  updateUserOwnerRel(userOwnerRel: UserOwnerRel, userOwnerId: number): Observable<any> {

    this.updateuserOwnerRelObservable = this.putData<EntityId>("userOwnerRel/" + userOwnerId, userOwnerRel).pipe(
      map((data) => {

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('updateUserOwnerRel error :' + err);
        this.updateuserOwnerRelObservable = null;
        return throwError(err);
      }
      ));
    return this.updateuserOwnerRelObservable;
  }

  // PUT userOwnerRel/{userOwnerId}/force
  updateUserOwnerRelForce(userOwnerRel: UserOwnerRel, userOwnerId: number): Observable<any> {

    this.userOwnerRelForceObservable = this.putData<EntityId>("userOwnerRel/" + userOwnerId + "/force", userOwnerRel).pipe(
      map((data) => {

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('updateUserOwnerRel error :' + err);
        this.userOwnerRelForceObservable = null;
        return throwError(err);
      }
      ));
    return this.userOwnerRelForceObservable;
  }

  // POST userOwner/{userOwnerId}/permissionsList/massive
  updateUserPermissionsMassive(userOwnerId: number): Observable<any> {

    this.userOwnerRelMassive = this.postData<any>("userOwnerRel/" + userOwnerId + "/permissionsList/massive", null).pipe(
      map((data) => {

        let devicesList;
        devicesList.deserialize(data);
        return devicesList;
      }
      )).pipe(catchError(err => {
        Log.debug('updateUserOwnerRel error :' + err);
        this.userOwnerRelMassive = null;
        return throwError(err);
      }
      ));
    return this.userOwnerRelMassive;
  }

  updateGroup(group: Group, groupId: number): Observable<any> {
    this.group = this.postData<EntityId>("group/", group).pipe(
      map((data) => {

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);

        Log.debug("updateGroup data: ");
        Log.debug(data);

        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('updategroup error :' + err);
        this.group = null;
        return throwError(err);
      }
      ));
    return this.group;

  }

  createOwner(ownerRegistrationData: OwnerRegistrationData): Observable<ControlUser> {

    this.userOwnerObservable = this.postData<ControlUser>('user/owner', ownerRegistrationData).pipe(
      map((data) => {

        this.userOwnerObservable = null;

        Log.debug('createOwner: ' + JSON.stringify(data));

        let owner = Object.create(ControlUser.prototype);
        owner.deserialize(data);

        Log.debug('owner: ' + JSON.stringify(owner));

        return owner;
      }
      )).pipe(catchError(err => {
        Log.debug('createOwner error :' + err);
        this.userOwnerObservable = null;
        return throwError(err);
      }
      ));
    return this.userOwnerObservable;
  }

  promoteUser(promoteUserData: ControlUser, email: string): Observable<ControlUser> {

    let userToPromote = this.postData<ControlUser>('user/' + email + '/owner', promoteUserData).pipe(
      map((data) => {

        Log.debug('promotedUser: ' + JSON.stringify(data));

        let promotedUser = Object.create(ControlUser.prototype);
        promotedUser.deserialize(data);

        Log.debug('promotedUser: ' + JSON.stringify(promotedUser));

        return promotedUser;
      }
      )).pipe(catchError(err => {
        Log.debug('promotedUser error :' + err);
        return throwError(err);
      }
      ));
    return userToPromote;
  }

  public getOwnerData(ownerUid: string): Observable<OwnerData> {

    if (this.ownerDataObservable) {

      Log.debug('getOwnerData data already loading');

      return this.ownerDataObservable;

    } else {

      Log.debug('getOwnerData no data');

      this.ownerDataObservable = this.getData<ItemsList<OwnerData>>("user/owner/" + ownerUid + "/data").pipe(
        map((data) => {

          this.ownerDataObservable = null;

          Log.debug('getOwnerData: ' + JSON.stringify(data));

          const o = Object.create(OwnerData.prototype);
          Object.assign(o, data);
          o.deserialize(data);

          Log.debug('owner: ' + JSON.stringify(o));

          return o;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwnerData error :' + err);
          this.ownerDataObservable = null;
          return throwError(err);
        }
        ));

      return this.ownerDataObservable;
    }
  }

  linkUserToOwner(user: ControlUser): Observable<ControlUser> {

    this.userOwnerObservable = this.postData<ControlUser>('user/{userUid}/owner/{ownerUid}"' + user.firebaseUid, user).pipe(
      map((data) => {

        this.userOwnerObservable = null;

        Log.debug('createOwner: ' + JSON.stringify(data));

        let owner = Object.create(ControlUser.prototype);
        owner.deserialize(data);

        Log.debug('owner: ' + JSON.stringify(owner));

        return owner;
      }
      )).pipe(catchError(err => {
        Log.debug('linkUserToOwner error :' + err);
        this.userOwnerObservable = null;
        return throwError(err);
      }
      ));
    return this.userOwnerObservable;
  }

  createUserInstallation(installation: ControlUserInstallation): Observable<EntityId> {

    Log.debug('createUserInstallation: ' + JSON.stringify(installation));

    this.userInstallationObservable = this.postData<ControlUserInstallation>('userInstallation', installation).pipe(
      map((data) => {

        this.userInstallationObservable = null;

        Log.debug('createUserInstallation: ' + JSON.stringify(data));

        let entityId = Object.create(EntityId.prototype);
        entityId.deserialize(data);

        return entityId;
      }
      )).pipe(catchError(err => {
        Log.debug('createUserInstallation error :' + err);
        this.userInstallationObservable = null;
        return throwError(err);
      }
      ));
    return this.userInstallationObservable;
  }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

    //const authPipeFactory: AuthPipeGenerator = next.data.authGuardPipe || (() => loggedIn);

    return this.afAuth.user.pipe(
      take(1),
      map(user => !!user),
      tap(loggedIn => {
        if (!loggedIn || this.meUser == null) {
          Log.debug('access denied')
          this.router.navigate(['login']);
        }
        else {

          // Log.debug(next.url[0].path);
        }
      })
      //map(canActivate => typeof canActivate == "boolean" ? canActivate : this.router.createUrlTree(canActivate))
    );
  }

  public getUserAccessMethods(userOwnerId: number): Observable<AccessMethod[]> {

    if (this.userAccessMethodsObservable) {
      Log.debug('getUserAccessMethods data already loading');
      return this.userAccessMethodsObservable;
    } else {
      this.userAccessMethodsObservable = this.getData<ItemsList<AccessMethod>>("userOwner/" + userOwnerId + "/accessMethod").pipe(
        map((data) => {

          this.userAccessMethodsObservable = null;

          Log.debug('getUserAccessMethods: ' + JSON.stringify(data));

          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new AccessMethod().deserialize(item));

          for (let i = 0; i < o.items.length; i++) {
            if (o.items[i].type == "Remote") {
              o.items[i].code = RemoteHelper.standardCodeToJcmCode(o.items[i].code);
            }
          }

          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('access methods error :' + err);
          this.userAccessMethodsObservable = null;
          return throwError(err);
        }
        ));

      return this.userAccessMethodsObservable;
    }
  }

  public getUserPermissionsCsv(userOwnerId: string): Observable<string> {

    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/userOwner/{userOwnerId}/permissions
    this.userPermissionsCsvObservable = this.getData<any>("owner/" + userOwnerId + "/user/csv").pipe(
      map((response) => {
        return response.data;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('user permissions csv error :' + err);
        this.userPermissionsCsvObservable = null;
        return throwError(err);
      }
      ));

    return this.userPermissionsCsvObservable;
    // }
  }

  public getUserPermissions(userOwnerId: number): Observable<UserPermissions[]> {
    // if (this.userPermissionsObservable) {

    //   Log.debug('getUserPermissionsObservable data already loading');
    //   return this.userPermissionsObservable;

    // } else {
    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/userOwner/{userOwnerId}/permissions
    this.userPermissionsObservable = this.getData<any>("userOwner/" + userOwnerId + "/permissions").pipe(
      map((data) => {
        var getPermissionsList: UserPermissions[] = [];

        this.userPermissionsObservable = null;

        for (let i = 0; i < data.items.length; i++) {

          let permissions: UserPermissions = Object.create(UserPermissions.prototype);
          permissions.deserialize(data.items[i]);

          getPermissionsList.push(permissions);
        }
        return getPermissionsList;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('user permissions error :' + err);
        this.userPermissionsObservable = null;
        return throwError(err);
      }
      ));

    return this.userPermissionsObservable;
    // }
  }

  public getGroupPermissions(groupId: number, ownerUid: string): Observable<GroupPermissions[]> {
    // if (this.groupPermissionsObservable) {

    //   Log.debug('getGroupPermissions data already loading');
    //   return this.groupPermissionsObservable;

    // } else {
    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/group/{id}/permissions
    this.groupPermissionsObservable = this.getData<any>("group/" + groupId + "/owner/" + ownerUid + "/permissions").pipe(
      map((data) => {
        var getPermissionsList: GroupPermissions[] = [];

        this.groupPermissionsObservable = null;

        for (let i = 0; i < data.items.length; i++) {

          let permissions: GroupPermissions = Object.create(GroupPermissions.prototype);
          permissions.deserialize(data.items[i]);

          getPermissionsList.push(permissions);
        }
        return getPermissionsList;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('group permissions error :' + err);
        this.groupPermissionsObservable = null;
        return throwError(err);
      }
      ));

    return this.groupPermissionsObservable;
    // }
  }


  public getUserActivationCodes(userOwnerId: number): Observable<ActivationCode[]> {
    if (this.activationCodesObservable) {

      Log.debug('getUserActivationCodes data already loading');
      return this.activationCodesObservable;

    } else {
      // /webAdmin/v1/userOwner/{userOwnerId}/codes
      this.activationCodesObservable = this.getData<ItemsList<ActivationCode>>("userOwner/" + userOwnerId + "/codes").pipe(
        map((data) => {
          var getActivationCodeList: ActivationCode[] = [];

          this.activationCodesObservable = null;

          for (let i = 0; i < data.items.length; i++) {

            let code: ActivationCode = Object.create(ActivationCode.prototype);
            code.deserialize(data.items[i]);

            getActivationCodeList.push(code);
          }
          return getActivationCodeList;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('get activation code error :' + err);
          this.activationCodesObservable = null;
          return throwError(err);
        }
        ));

      return this.activationCodesObservable;
    }
  }

  public postActivationCode(userOwnerId: string): Observable<ActivationCode> {
    //POST /webAdmin/v1/userOwner/{userOwnerId}/code
    Log.debug("postActivationCode owner id");
    Log.debug(userOwnerId);
    this.newActivationCodesObservable = this.postData<any>("userOwner/" + userOwnerId + "/code", null).pipe(
      map((data) => {
        // this.newActivationCodesObservable = data.code;

        Log.debug("postActivationCode code response");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('postActivationCode error :' + err);
        this.newActivationCodesObservable = null;
        return throwError(err);
      }
      ));
    return this.newActivationCodesObservable;
  }

  public putActivationCode(userOwnerId: string, code: ActivationCode): Observable<ActivationCode> {
    //POST /webAdmin/v1/userOwner/{userOwnerId}/code
    Log.debug("putActivationCode owner id");
    Log.debug(userOwnerId);
    Log.debug("putActivationCode code");
    Log.debug(code);

    this.updatedActivationCodesObservable = this.putData<any>("userOwner/" + userOwnerId + "/code", code).pipe(
      map((data) => {

        Log.debug("putActivationCode code response");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('putActivationCode error :' + err);
        this.updatedActivationCodesObservable = null;
        return throwError(err);
      }
      ));
    return this.updatedActivationCodesObservable;
  }

  public deleteActivationCode(userOwnerId: string, code: string): Observable<ActivationCode> {
    // DELETE /webAdmin/v1/userOwner/{userOwnerId}/code/{code}
    Log.debug("deleteActivationCode owner id");
    Log.debug(userOwnerId);
    let activationCodesObservableToDelete
    activationCodesObservableToDelete = this.deleteData<any>("userOwner/" + userOwnerId + "/code/" + code).pipe(
      map((data) => {
        Log.debug("deleteActivationCode code response");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      ));
    return activationCodesObservableToDelete;
  }

  public deleteUser(ownerUid: string, userOwnerId: number): Observable<ActivationCode> {
    // DELETE /webAdmin/v1/userOwnerRel/{userOwnerId}
    Log.debug("deleteActivationCode owner id");
    Log.debug(userOwnerId);
    let userToDelete
    userToDelete = this.deleteData<any>("userOwnerRel/" + userOwnerId).pipe(
      map((data) => {
        Log.debug("delete user code response");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      ));
    return userToDelete;
  }

  public deleteGroup(groupId: number, ownerUid: string): Observable<ActivationCode> {
    //  group/{id}/owner/{ownerUid}
    Log.debug("deleteGroup  id");
    Log.debug(groupId);
    let groupToDelete;
    groupToDelete = this.deleteData<any>("group/" + groupId + "/owner/" + ownerUid).pipe(
      map((data) => {
        Log.debug("deleteGroup response");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity;
      }
      )).pipe(catchError(err => {
        Log.debug('deleteGroup error' + err);
        return throwError(err.error.message);
      }
      ));
    return groupToDelete;
  }

  // public deleteGroup(): Observable<ActivationCode> {
  //   // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/group/{id}
  // }

  public isMethodUsedCheck(userOwnerId: string, accessMethod: AccessMethod): Observable<boolean> {

    // POST  /webAdmin/v1/userOwner/{userOwnerId}/accessMethod/check;
    let obs = this.postData<any>("userOwner/" + userOwnerId + "/accessMethod/check", accessMethod).pipe(
      map((data) => {
        Log.debug("isMethodUsedCheck");
        Log.debug(data);

        let entity = Object.create(EntityId.prototype);
        entity.deserialize(data);
        return entity.id == 0;
      }
      )).pipe(catchError(err => {
        Log.debug('isMethodUsedCheck error :' + err);
        return of(false);
      }
      ));
    return obs;
  }

  public searchByMethod(code: string, type: string, ownerUid: string): Observable<UserOwnerRel> {
    Log.debug('UserService : searchByMethod');
    // https://onecontrol-cloud-pilomat.appspot.com/_ah/api/webAdmin/v1/user/method/{type}/{code}/owner/{ownerUid}
    let userFound = this.getData<UserOwnerRel>('user/method/' + type + '/' + code + '/owner/' + ownerUid).pipe(
      map((data) => {
        return data;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('searchByMethod error :' + err);
        return throwError(err);
      }
      ));
    return userFound;
  }

  getOperationLogCount(ownerUid: string, minDate: string, maxDate: string): Observable<EntityCount> {
    if (this.operationLogCountObservable) {
      Log.debug('getOperationLogCount data already loading');
      return this.operationLogCountObservable;
    } else {
      // GET /webAdmin/v1/operationLogs/owner/{ownerUid}/{mindate}/{maxDate}/count
      this.operationLogCountObservable = this.getData<EntityCount>("operationLogs/owner/" + ownerUid + "/" + minDate + "/" + maxDate + "/count").pipe(
        map((data) => {
          Log.debug("Log count: " + JSON.stringify(data));

          this.operationLogCountObservable = null;

          let entityCount = Object.create(EntityCount.prototype);
          entityCount.deserialize(data);

          return entityCount;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getLog error :' + err);
          this.operationLogCountObservable = null;
          return throwError(err);
        }
        ));
    }
    return this.operationLogCountObservable;
  }

  public getOperations(ownerUid: string, minDate: string, maxDate: string, page: number) {
    if (this.operationLogObservable) {

      Log.debug('getOperations data already loading');

      return this.operationLogObservable;

    } else {

      Log.debug('getLog no data');

      // GET /webAdmin/v1/operationLogs/{mindate}/{maxDate}/page/{page}
      this.operationLogObservable = this.getData<ItemsList<operationLogEvent[]>>("operationLogs/owner/" + ownerUid + "/" + minDate + "/" + maxDate + "/page/" + page).pipe(
        map((data) => {

          this.operationLogObservable = null;

          //Log.debug('getLog: ' + JSON.stringify(data));

          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new operationLogEvent().deserialize(item));

          //Log.debug('log: ' + JSON.stringify(o.items));

          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getLog error :' + err);
          this.operationLogObservable = null;
          return throwError(err);
        }
        ));

      return this.operationLogObservable;
    }
  }

  getOperationLogByOperatorCount(ownerUid: string, operator: string, minDate: string, maxDate: string): Observable<EntityCount> {
    if (this.operationLogByOperatorCountObservable) {
      Log.debug('getOperationLogCount data already loading');
      return this.operationLogByOperatorCountObservable;
    } else {
      // /webAdmin/v1/operationLogs/operator/{operator}/{mindate}/{maxDate}/count
      this.operationLogByOperatorCountObservable = this.getData<EntityCount>("operationLogs/owner/" + ownerUid + "/operator/" + operator + "/" + minDate + "/" + maxDate + "/count").pipe(
        map((data) => {
          Log.debug("Log count: " + JSON.stringify(data));

          this.operationLogByOperatorCountObservable = null;

          let entityCount = Object.create(EntityCount.prototype);
          entityCount.deserialize(data);

          return entityCount;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getLog error :' + err);
          this.operationLogByOperatorCountObservable = null;
          return throwError(err);
        }
        ));
    }
    return this.operationLogByOperatorCountObservable;
  }

  public getOperationsByOperator(ownerUid: string, operator: string, minDate: string, maxDate: string, page: number) {
    if (this.operationLogByOperatorObservable) {

      Log.debug('getOperations data already loading');

      return this.operationLogByOperatorObservable;

    } else {

      Log.debug('getLog no data');

      // /webAdmin/v1/operationLogs/operator/{operator}/{mindate}/{maxDate}/page/{page}
      this.operationLogByOperatorObservable = this.getData<ItemsList<operationLogEvent[]>>("operationLogs/owner/" + ownerUid + "/operator/" + operator + "/" + minDate + "/" + maxDate + "/page/" + page).pipe(
        map((data) => {

          this.operationLogByOperatorObservable = null;

          //Log.debug('getLog: ' + JSON.stringify(data));

          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new operationLogEvent().deserialize(item));

          //Log.debug('log: ' + JSON.stringify(o.items));

          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getLog error :' + err);
          this.operationLogByOperatorObservable = null;
          return throwError(err);
        }
        ));

      return this.operationLogByOperatorObservable;
    }
  }

  getOperationLogBySerialCount(ownerUid: string, serial: string, minDate: string, maxDate: string): Observable<EntityCount> {
    if (this.operationLogBySerialCountObservable) {
      Log.debug('getOperationLogCount data already loading');
      return this.operationLogBySerialCountObservable;
    } else {
      // /webAdmin/v1/operationLogs/serial/{serial}/{mindate}/{maxDate}/count
      this.operationLogBySerialCountObservable = this.getData<EntityCount>("operationLogs/owner/" + ownerUid + "/serial/" + serial + "/" + minDate + "/" + maxDate + "/count").pipe(
        map((data) => {
          Log.debug("Log count: " + JSON.stringify(data));

          this.operationLogBySerialCountObservable = null;

          let entityCount = Object.create(EntityCount.prototype);
          entityCount.deserialize(data);

          return entityCount;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getLog error :' + err);
          this.operationLogBySerialCountObservable = null;
          return throwError(err);
        }
        ));
    }
    return this.operationLogBySerialCountObservable;
  }

  public getOperationsBySerial(ownerUid: string, serial: string, minDate: string, maxDate: string, page: number) {
    if (this.operationLogBySerialObservable) {

      Log.debug('getOperations data already loading');

      return this.operationLogBySerialObservable;

    } else {

      Log.debug('getLog no data');

      // /webAdmin/v1/operationLogs/serial/{serial}/{mindate}/{maxDate}/page/{page}
      this.operationLogBySerialObservable = this.getData<ItemsList<operationLogEvent[]>>("operationLogs/owner/" + ownerUid + "/serial/" + serial + "/" + minDate + "/" + maxDate + "/page/" + page).pipe(
        map((data) => {

          this.operationLogBySerialObservable = null;

          //Log.debug('getLog: ' + JSON.stringify(data));

          const o = Object.create(ItemsList.prototype);
          Object.assign(o, data);
          o.items = data.items.map(item => new operationLogEvent().deserialize(item));

          //Log.debug('log: ' + JSON.stringify(o.items));

          return o.items;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getLog error :' + err);
          this.operationLogBySerialObservable = null;
          return throwError(err);
        }
        ));

      return this.operationLogBySerialObservable;
    }
  }

  getOwnerDefaultEmailData(ownerUid, language): Observable<EmailDefaultData> {

    if (this.getOwnerDefaultEmailObservable) {

      Log.debug('getOwnerDefaultEmail data already loading');

      return this.getOwnerDefaultEmailObservable;

    } else {

      Log.debug('getOwnerDefaultEmail no data');

      // GET  owner/{ownerUid}/lang/{language}/email
      this.getOwnerDefaultEmailObservable = this.getData<EmailDefaultData>("owner/" + ownerUid + "/lang/" + language + "/email").pipe(
        map((data) => {

          Log.debug("\n\nEmailDefaultData: ");
          Log.debug(data);


          return data;
        }
        ))
        .pipe(catchError(err => {
          Log.debug('getOwnerDefaultEmail error :' + err);
          this.getOwnerDefaultEmailObservable = null;
          return throwError(err);
        }
        ));

      return this.getOwnerDefaultEmailObservable;
    }
  }

  setOwnerDefaultEmailData(ownerUid, emaildefaultData): Observable<any> {
    // POST  owner/{ownerUid}/email
    this.setOwnerDefaultEmailObservable = this.postData<any>("owner/" + ownerUid + "/email/", emaildefaultData).pipe(
      map((data) => {

        Log.debug("\n\nresponse: ");
        Log.debug(data);

        this.setOwnerDefaultEmailObservable = data;

        return this.setOwnerDefaultEmailObservable;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('setOwnerDefaultEmailData error :' + err);
        this.setOwnerDefaultEmailObservable = null;
        return throwError(err);
      }
      ));

    return this.setOwnerDefaultEmailObservable;
  }

  sendEmail(emailData: EmailData): Observable<EntityId> {
    this.emailSentObservable = this.postData<EmailData>("email", emailData).pipe(
      map((data) => {

        this.emailSentObservable = null;

        Log.debug('sendEmail: ' + JSON.stringify(data));
        // localStorage.setItem("Group", JSON.stringify(this.meGroup));

        return data;
      }
      )).pipe(catchError(err => {
        Log.debug('sendEmail error :' + err);
        this.emailSentObservable = null;
        return throwError(err);
      }
      ));

    return this.emailSentObservable;
  }

  sendActivationCodeEmail(emailData: EmailData, codeId: string, userOwnerId: string): Observable<EntityId> {
    // /webAdmin/v1/userOwner/:userOwnerId/email/code/:codeId
    this.emailSentObservable = this.postData<EmailData>("userOwner/" + userOwnerId + "/email/code/" + codeId, emailData).pipe(
      map((data) => {

        this.emailSentObservable = null;

        Log.debug('sendActivationCodeEmail: ' + JSON.stringify(data));
        // localStorage.setItem("Group", JSON.stringify(this.meGroup));

        return data;
      }
      )).pipe(catchError(err => {
        Log.debug('sendActivationCodeEmail error :' + err);
        this.emailSentObservable = null;
        return throwError(err);
      }
      ));

    return this.emailSentObservable;
  }


  createNewUserRequest(newUserRequest, ownerUid): Observable<any> {
    // POST /public/v1/join/{ownerUid}
    this.createNewUserRequestObservable = this.postPublicData<any>("join/" + ownerUid, newUserRequest).pipe(
      map((data) => {

        Log.debug("\n\nresponse: ");
        Log.debug(data);

        this.createNewUserRequestObservable = data;

        return this.createNewUserRequestObservable;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('setOwnerDefaultEmailData error :' + err);
        this.createNewUserRequestObservable = null;
        return throwError(err);
      }
      ));

    return this.createNewUserRequestObservable;
  }

  insertNewUser(newUserRequest, ownerUid): Observable<any> {
    // POST /public/v1/request/{ownerUid}
    this.createNewUserRequestObservable = this.postData<any>("request/" + ownerUid, newUserRequest).pipe(
      map((data) => {

        Log.debug("\n\nresponse: ");
        Log.debug(data);

        this.createNewUserRequestObservable = data;

        return this.createNewUserRequestObservable;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('setOwnerDefaultEmailData error :' + err);
        this.createNewUserRequestObservable = null;
        return throwError(err);
      }
      ));

    return this.createNewUserRequestObservable;
  }

  autoAcceptRequest(newUserRequest: NewUserRequestResult): Observable<any> {
    // POST request/acceptReject/{newUserRequestId}
    this.acceptRejectNewUserRequestObservable = this.postData<any>("request/acceptReject/" + newUserRequest.id.toString(), newUserRequest).pipe(
      map((data) => {

        Log.debug("\n\nresponse: ");
        Log.debug(data);

        this.acceptRejectNewUserRequestObservable = data;

        return this.acceptRejectNewUserRequestObservable;
      }
      ))
      .pipe(catchError(err => {
        if (err.error.message == "code-already-used") {

          return throwError("code-already-used");

          //Se mi viene passato il metodo duplicato ritorno il messaggio per intero
        } else if (err.error.message.startsWith("code-already-used")) {

          return throwError(err.error.message);

        } else if (err.error.message == "invalid-user") {

          return throwError("invalid-user");

        } else {
          Log.debug('setOwnerDefaultEmailData error :' + err);
          this.acceptRejectNewUserRequestObservable = null;
          return throwError(err);
        }
      }
      ));

    return this.acceptRejectNewUserRequestObservable;

  }

  deleteTransmitRequest(requestId: number): Observable<any> {
    // POST request/acceptReject/{newUserRequestId}
    this.deleteRequestObservable = this.deleteData<any>("request/" + requestId.toString()).pipe(
      map((data) => {

        Log.debug("\n\nresponse: ");
        Log.debug(data);

        this.deleteRequestObservable = data;

        return this.deleteRequestObservable;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('setOwnerDefaultEmailData error :' + err);
        this.deleteRequestObservable = null;
        return throwError(err);
      }
      ));

    return this.deleteRequestObservable;

  }


  getNewUserRequests(ownerUid): Observable<NewUserRequest[]> {

    // get /webAdmin/v1/requests/owner/{ownerUid}

    this.getNewUserRequestsObservable = this.getData<any>("requests/owner/" + ownerUid).pipe(
      map((data) => {

        const o = Object.create(ItemsList.prototype);
        Object.assign(o, data);
        o.items = data.items.map(item => new NewUserRequest().deserialize(item));


        this.getNewUserRequestsObservable = o.items;

        return this.getNewUserRequestsObservable;
      }
      ))
      .pipe(catchError(err => {
        Log.debug('getNewUserRequests error :' + err);
        this.getNewUserRequestsObservable = null;
        return throwError(err);
      }
      ));

    return this.getNewUserRequestsObservable;
  }

  sendRequestApproval(NewUserRequestResult, requestId): Observable<NewUserRequest> {
    // post /webAdmin/v1/request/acceptReject/{newUserRequestId}
    this.acceptRejectNewUserRequestObservable = this.postData<any>("request/acceptReject/" + requestId, NewUserRequestResult).pipe(
      map((data) => {

        this.acceptRejectNewUserRequestObservable = data;

        return this.acceptRejectNewUserRequestObservable;
      }
      ))
      .pipe(catchError(err => {

        if (err.error.message == "code-already-used") {

          return throwError("code-already-used");

          //Se mi viene passato il metodo duplicato ritorno il messaggio per intero
        } else if (err.error.message.startsWith("code-already-used")) {

          return throwError(err.error.message);

        } else if (err.error.message == "invalid-user") {

          return throwError("invalid-user");

        } else {
          Log.debug('setOwnerDefaultEmailData error :' + err);
          this.acceptRejectNewUserRequestObservable = null;
          return throwError(err);
        }
      }
      ));

    return this.acceptRejectNewUserRequestObservable;
  }


  getNewUserRequestPermissionType(ownerUid): Observable<NewUserRequestPermissionType[]> {
    // get /webAdmin/v1/requests/type/owner/{ownerUid}
    this.getNewUserRequestPermissionTypeObservable = this.getData<any>("requests/type/owner/" + ownerUid).pipe(
      map((data) => {

        const o = Object.create(ItemsList.prototype);
        Object.assign(o, data);
        o.items = data.items.map(item => new NewUserRequestPermissionType().deserialize(item));


        this.getNewUserRequestPermissionTypeObservable = o.items;

        return this.getNewUserRequestPermissionTypeObservable;
      }
      ))
      .pipe(catchError(err => {

        Log.debug('getNewUserRequestPermissionType error :' + err);
        this.getNewUserRequestPermissionTypeObservable = null;
        return throwError(err);

      }
      ));

    return this.getNewUserRequestPermissionTypeObservable;
  }

    getNewUserRequestPermissionTypePublic(ownerUid): Observable<NewUserRequestPermissionType[]> {
    // get /webAdmin/v1/requests/type/owner/{ownerUid}
    this.getNewUserRequestPermissionTypeObservable = this.getPublicData<any>("requests/type/owner/" + ownerUid).pipe(
      map((data) => {

        const o = Object.create(ItemsList.prototype);
        Object.assign(o, data);
        o.items = data.items.map(item => new NewUserRequestPermissionType().deserialize(item));


        this.getNewUserRequestPermissionTypeObservable = o.items;

        return this.getNewUserRequestPermissionTypeObservable;
      }
      ))
      .pipe(catchError(err => {

        Log.debug('getNewUserRequestPermissionType error :' + err);
        this.getNewUserRequestPermissionTypeObservable = null;
        return throwError(err);

      }
      ));

    return this.getNewUserRequestPermissionTypeObservable;
  }

  createNewUserRequestPermissionType(ownerUid, newUserRequestPermissionType): Observable<EntityId> {
    // post /webAdmin/v1/request/type/{ownerUid}
    this.createNewUserRequestPermissionTypeObservable = this.postData<any>("request/type/" + ownerUid, newUserRequestPermissionType).pipe(
      map((data) => {

        this.createNewUserRequestPermissionTypeObservable = data;

        return this.createNewUserRequestPermissionTypeObservable;
      }
      ))
      .pipe(catchError(err => {

        Log.debug('createNewUserRequestPermissionType error :' + err);
        this.createNewUserRequestPermissionTypeObservable = null;
        return throwError(err);

      }
      ));

    return this.createNewUserRequestPermissionTypeObservable;
  }

  deleteNewUserRequestPermissionType(id): Observable<EntityId> {
    // post /webAdmin/v1/request/type/{ownerUid}
    this.deleteNewUserRequestPermissionTypeObservable = this.deleteData<any>("request/type/" + id).pipe(
      map((data) => {

        this.deleteNewUserRequestPermissionTypeObservable = data;

        return this.deleteNewUserRequestPermissionTypeObservable;
      }
      ))
      .pipe(catchError(err => {

        Log.debug('createNewUserRequestPermissionType error :' + err);
        this.deleteNewUserRequestPermissionTypeObservable = null;
        return throwError(err);

      }
      ));

    return this.deleteNewUserRequestPermissionTypeObservable;
  }

}

